"use client";

import { useState } from "react";
import { trackEvent } from "@/lib/analytics";

/**
 * Saved-link strip for value surfaces: gives a persisted result a visible
 * return/share path (rule 46 — the value moment keeps a durable URL).
 * Used below the inline kundali result (→ /kundali/<id>) and on the Gun
 * Milan results (→ /match/<id>). Navigation affordance, not an offer.
 */
export default function SavedLinkCard({
  path,
  heading,
  body,
  shareLabel,
  copiedLabel,
  analyticsEvent,
  analyticsProps,
}: {
  path: string;
  heading: string;
  body: string;
  shareLabel: string;
  copiedLabel: string;
  analyticsEvent: string;
  analyticsProps?: Record<string, unknown>;
}) {
  const [copied, setCopied] = useState(false);

  async function handleShare() {
    const url = `${window.location.origin}${path}`;
    trackEvent(analyticsEvent, { path, ...analyticsProps });
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ title: heading, url });
        return;
      } catch {
        /* user dismissed the share sheet — fall through to copy */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      /* clipboard unavailable — the visible link below still works */
    }
  }

  return (
    <div className="card-sacred p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
      <div className="min-w-0">
        <p className="text-[14px] font-bold text-ish-dark">{heading}</p>
        <p className="mt-1 text-[12px] leading-relaxed text-ish-text-muted">{body}</p>
        <p className="mt-1 text-[12px] break-all">
          <a href={path} className="underline text-ish-accent">
            {path}
          </a>
        </p>
      </div>
      <button
        type="button"
        onClick={handleShare}
        className="btn-sacred shrink-0 font-semibold py-2.5 px-5 rounded-lg text-[13px]"
      >
        {copied ? copiedLabel : shareLabel}
      </button>
    </div>
  );
}
